import React, { Component, Fragment, useEffect, useMemo, useRef } from 'react';
import { Animated, Image, View } from 'react-native';
import { imageUtils } from '../../utils/imageUtils';
import Svg, {
    Circle,
    CircleProps,
    Defs,
    LinearGradient,
    Stop,
} from 'react-native-svg';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import { seconds } from '../../utils/animations';

class Bar extends Component<{ id: string; percentage: number }> {
    render(): React.ReactNode {
        return (
            <Circle
                cx={0}
                cy={0}
                r={46}
                strokeLinecap="round"
                fill="none"
                stroke={`url(#${this.props.id})`}
                strokeWidth={this.props.percentage === 0 ? 0 : 6}
                strokeDasharray={`${this.props.percentage} 1000000000`}
                translateX={50}
                translateY={50}
                rotation={-172}
            />
        );
    }
}

class Thumb extends Component<CircleProps & { rotate: number }> {
    render(): React.ReactNode {
        return <Circle {...this.props} rotation={this.props.rotate} />;
    }
}

const AnimatedBar = Animated.createAnimatedComponent(Bar);
const AnimatedThumb = Animated.createAnimatedComponent(Thumb);

type CircularSliderProps = {
    size?: number;
    step?: number;
    percentage?: number;
    colors?: [string, string];
    onChange?: (percentage: number) => void;
    enabled?: boolean;
    animated?: boolean;
    delay?: number;
};

export default function CircularSlider({
    size = 200,
    step = 1,
    percentage,
    onChange,
    colors = ['#0495CD', '#2EB0E2'],
    enabled = true,
    animated = false,
    delay = 0,
}: CircularSliderProps) {
    const animatedValue = useRef(new Animated.Value(0)).current;
    const id = useMemo(() => `${Math.random()}-${Date.now()}`, []);

    const circularPercentage = useMemo(() => {
        if (!animated) {
            return Math.floor(((percentage || 0) * 134) / 100);
        }
        return animatedValue.interpolate({
            inputRange: [0, 1],
            outputRange: [0, Math.floor(((percentage || 50) * 134) / 100)],
        });
    }, [animated, animatedValue, percentage]);

    const rotation = useMemo(() => {
        if (!animated) {
            return ((percentage || 0) * 170) / 100 + 5;
        }
        return animatedValue.interpolate({
            inputRange: [0, 1],
            outputRange: [0, ((percentage || 50) * 170) / 100 + 5],
        });
    }, [animated, animatedValue, percentage]);

    useEffect(() => {
        if (animated) {
            Animated.timing(animatedValue, {
                toValue: 1,
                duration: seconds(1),
                useNativeDriver: false,
                delay,
            }).start();
        }
    }, [animated, animatedValue, delay]);

    const gesture = Gesture.Pan()
        .enabled(enabled)
        .runOnJS(true)
        .onUpdate((ev) => {
            const x = ev.x;
            const y = ev.y;
            const cx = size / 2;
            const cy = size / 2;
            if (y > size / 2) {
                return;
            }
            if (
                Math.pow(x - cx, 2) +
                    Math.pow(y - cy, 2) -
                    Math.pow(size / 2, 2) <
                10
            ) {
                const radianAngle = Math.atan2(y - cy, x - cx);
                const angle = Math.floor(
                    Math.abs(radianAngle * (180 / Math.PI)),
                );
                const newPercentage = Math.floor(100 - (angle * 100) / 180);
                if (newPercentage % step === 0) {
                    onChange?.(newPercentage);
                }
            }
        });

    return (
        <GestureDetector gesture={gesture}>
            <View style={{ width: size, height: size / 2 }}>
                <Image
                    style={{
                        position: 'absolute',
                        left: 3,
                        top: 0,
                        width: '98%',
                        height: '100%',
                    }}
                    source={imageUtils.screens.quarterSliderMask}
                    resizeMode="contain"
                />
                <Svg width={size} height={size / 2} viewBox="-1 0 102 50">
                    <AnimatedBar id={id} percentage={circularPercentage} />
                    {enabled && (
                        <Fragment>
                            <AnimatedThumb
                                fill="#D6E3F3"
                                r={5}
                                cx={-46}
                                cy={0}
                                translateX={50}
                                translateY={50}
                                rotate={rotation}
                            />
                            <AnimatedThumb
                                fill={colors[1]}
                                r={4.5}
                                cx={-46}
                                cy={0}
                                translateX={50}
                                translateY={50}
                                rotate={rotation}
                            />
                            <AnimatedThumb
                                fill="#D6E3F3"
                                r={3.5}
                                cx={-46}
                                cy={0}
                                translateX={50}
                                translateY={50}
                                rotate={rotation}
                            />
                            <AnimatedThumb
                                fill={colors[0]}
                                r={3}
                                cx={-46}
                                cy={0}
                                translateX={50}
                                translateY={50}
                                rotate={rotation}
                            />
                        </Fragment>
                    )}
                    <Defs>
                        <LinearGradient
                            id={id}
                            x1={0}
                            y1={50}
                            x2={30}
                            y2={50}
                            gradientUnits="userSpaceOnUse"
                        >
                            <Stop stopColor={colors[0]} />
                            <Stop offset={1} stopColor={colors[1]} />
                        </LinearGradient>
                    </Defs>
                </Svg>
            </View>
        </GestureDetector>
    );
}
